import React, { useState, useEffect, ReactElement } from "react";
import "assets/custom_styles.css";
import axios, { setAuthToken } from "../src/api";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Sidenav from "examples/Sidenav";
//@ts-ignore
import Configurator from "examples/Configurator";
//@ts-ignore
import baseTheme from "assets/theme";
import { useSoftUIController, setMiniSidenav } from "context";
import brand from "../src/assets/images/scoutlogo.png";
import ForgotPassword from "layouts/authentication/passwords/ForgotPassword";
import PasswordReset from "layouts/authentication/passwords/PasswordReset";
import { useSelector, useDispatch } from "react-redux";
import { userLogin } from "store/reducers/auth";
import { privateRoutes } from "routes";
import AdminSignIn from "layouts/authentication/admin-sign-in/AdminSignIn";
import NotFound from "layouts/NotFound";
import PageLoader from "examples/ReuseFunctions/PageLoader";
import Home from "layouts/Home";
import About from "layouts/Home/About";
import Contact from "layouts/Home/Contact";
import PrivacyPolicy from "layouts/Home/PrivacyPolicy";
import TermsConditions from "layouts/Home/TermsConditions";
import ReUsePage, { type PageData } from "layouts/Home/ReUsePage";
import AllPages from "examples/ReuseFunctions/AllPages";
import { RootState } from "store";
import { ThemeColorProvider } from "context/ThemeContext";
type AppRoute = {
  route?: string;
  component?: ReactElement;
  collapse?: AppRoute[];
  key?: string | number;
  [key: string]: any;
};
const DEFAULT_THEME_COLOR = "#2e7d32";

export default function App() {
  const { pagesData, logo } = AllPages();
  const normalizedPagesData: PageData[] = Array.isArray(pagesData)
    ? (pagesData as PageData[])
    : [];
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [themeColor, setThemeColor] = useState<string>(DEFAULT_THEME_COLOR);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) return;
    const apiBase =
      globalThis.location?.host === "localhost:3000"
        ? "http://localhost:5000/api"
        : "/api";
    fetch(`${apiBase}/client/theme`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        if (data?.theme_color) setThemeColor(data.theme_color);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--primary-color",
      themeColor || DEFAULT_THEME_COLOR
    );
  }, [themeColor]);

  useEffect(() => {
    const getAuthUser = () => {
      const token = localStorage.getItem("token");
      if (!token) {
        dispatch(userLogin({ user: null }));
        setLoading(false);
        return;
      }

      setAuthToken(token);
      axios
        .get("/auth")
        .then((response) => {
          const authUser = response.data;
          dispatch(userLogin({ user: authUser }));
          setThemeColor(
            authUser?.client_theme_color || DEFAULT_THEME_COLOR
          );
          setLoading(false);
        })
        .catch((error) => {
          if (error?.response?.status === 402) {
            setAuthToken(null);
            const msg =
              error?.response?.data?.error ||
              "No active subscription. Please contact super admin.";
            try {
              sessionStorage.setItem("authError", msg);
            } catch (_) {}
          }
          dispatch(userLogin({ user: null }));
          setLoading(false);
        });
    };
    getAuthUser();
  }, [dispatch]);

  const [controller, dispatched] = useSoftUIController();
  const { miniSidenav, direction, layout, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatched, false);
      setOnMouseEnter(true);
    }
  };
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatched, true);
      setOnMouseEnter(false);
    }
  };
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    (document.scrollingElement || document.documentElement).scrollTop = 0;
  }, [pathname]);

  // Table header: no background. Re-inject on route change so it wins over mui-datatables makeStyles.
  useEffect(() => {
    const id = "scout-table-head-no-bg";
    const css =
      "thead .MuiTableCell-root, thead .MuiTableCell-head, .MuiTableHead-root .MuiTableCell-root, " +
      "[class*=\"MUIDataTable\"] thead .MuiTableCell-root, [class*=\"MUIDataTable\"] thead td, " +
      "[class*=\"MUIDataTableHeadCell\"], table thead th, table thead td " +
      "{ background-color: transparent !important; background: none !important; }";
    const run = () => {
      document.getElementById(id)?.remove();
      const styleEl = document.createElement("style");
      styleEl.id = id;
      styleEl.textContent = css;
      document.body.appendChild(styleEl);
    };
    run();
    const t = setTimeout(run, 500);
    return () => {
      clearTimeout(t);
      document.getElementById(id)?.remove();
    };
  }, [pathname]);
  const shouldRenderSidenav = (
    pathname: string | string[],
    userRole: string | undefined
  ) => {
    if (!userRole) {
      return false;
    }
    const routeAccess: Record<string, string[]> = {
      "/stockmanagers-list": ["admin", "superAdmin"],
      "/add-stockmanager": ["admin"],
      "/edit-stockmanager": ["admin"],
      "/supervisors-list": ["admin"],
      "/add-supervisor": ["admin"],
      "/edit-supervisor": ["admin"],
      "/pages-list": ["admin"],
      "/add-page": ["admin", "superAdmin"],
      "/edit-page": ["admin", "superAdmin"],
      "/assets-list": ["admin", "supervisor", "superAdmin"],
      "/add-subscriptiontype": ["admin", "superAdmin"],
      "/admin/employee-closed-tickets": ["admin", "supervisor", "superAdmin"],
      "/employee/ticket-view": ["employee"],
      "/employee-tickets-list": ["employee"],
      "/dashboard": [
        "admin",
        "supervisor",
        "employee",
        "stockManager",
        "superAdmin",
        "BranchHead",
      ],
      "/subscribe": ["admin"],
      "/admin/profile": [
        "admin",
        "supervisor",
        "employee",
        "stockManager",
        "superAdmin",
        "BranchHead",
      ],
      "/admin/edit-ticket": ["admin", "supervisor", "employee", "superAdmin"],
      "/admin/tickets-list": ["admin", "supervisor", "superAdmin"],
      "/general-preference": ["admin", "supervisor", "superAdmin"],
      "/employees-list": ["admin", "supervisor", "superAdmin"],
      "/add-employee": ["admin", "supervisor", "superAdmin"],
      "/edit-employee": ["admin", "supervisor", "superAdmin"],
      "/customers-list": ["admin", "supervisor", "superAdmin"],
      "/subscriptions-list": ["admin", "supervisor", "superAdmin"],
      "/customer-subscriptions-list": ["customer"],
      "/add-subscription": ["admin", "supervisor", "superAdmin"],
      "/edit-subscription": ["admin", "supervisor", "superAdmin"],
      "/add-customer": ["admin", "supervisor", "superAdmin"],
      "/edit-customer": ["admin", "supervisor", "superAdmin"],
      "/admin/add-ticket": ["admin", "supervisor", "superAdmin"],
      "/locations": ["admin", "supervisor", "superAdmin"],
      "/admin/ticket-view": ["admin", "supervisor", "superAdmin"],
      "/address-view": ["admin", "supervisor", "superAdmin"],
      "/templates-list": ["admin", "supervisor", "superAdmin"],
      "/types": ["admin", "supervisor", "superAdmin"],
      "/add-ticket": ["admin", "customer", "superAdmin"],
      "/customer/tickets-list": ["customer"],
      "/customer/edit-ticket": ["customer"],
      "/customer/ticket-view": ["customer", "BranchHead"],
      "/customer/profile": ["customer"],
      "/customer/assets-list": ["customer"],
      "/customer/assets-view": ["customer", "BranchHead"],
      "/calender": ["employee"],
      "/eventsoverview": ["employee"],
      "/spare-parts-list": ["stockManager"],
      "/orders-list": ["stockManager"],
      "/view-order": ["stockManager"],
      "/spare-parts-request": ["stockManager"],
      "/assets/view": ["admin", "superAdmin"],
      "/renewal-list": ["admin", "supervisor", "superAdmin"],
      "/divisions-view": ["admin", "supervisor", "superAdmin"],
      "/view-subscription": ["customer", "BranchHead"],
      "/upcoming-free-service": ["admin", "supervisor", "superAdmin"],
      "/expiring-subscriptions-list": ["admin", "supervisor", "superAdmin"],
      "/branch-head/tickets-list": ["BranchHead"],
      "/branch-head/add-ticket": ["BranchHead"],
      "/branch-head/assets-list": ["BranchHead"],
      "/branch-head/subscriptions-list": ["BranchHead"],
      "/admin/ticket-images": ["admin", "superadmin"],
      "/customer/view": ["admin", "superadmin", "supervisor"],
      "/customer-divisions-view/:id": [
        "admin",
        "superadmin",
        "supervisor",
        "customer",
      ],
      "/customer-members-view/:id": [
        "admin",
        "superadmin",
        "supervisor",
        "customer",
      ],
    };

    for (const routePath in routeAccess) {
      if (
        pathname.includes(routePath) &&
        routeAccess[routePath].includes(userRole)
      ) {
        return true;
      }
    }
    return false;
  };

  const renderSidenav = shouldRenderSidenav(pathname, user?.Role[0]);
  const renderRoutes = (routes: AppRoute[]): React.ReactNode =>
    routes.map((route, index) => {
      if (route.collapse) {
        if (!route.route) return null;
        return renderRoutes(route.collapse);
      }
      if (route.route) {
        if (user) {
          if (shouldRenderSidenav(route.route, user?.Role[0])) {
            return (
              <Route
                path={route.route}
                element={route.component}
                key={route.key || index}
              />
            );
          } else {
            return (
              <Route
                path={route.route}
                element={
                  <Navigate
                    to={user.roleId === 5 ? "/profile" : "/dashboard"}
                  />
                }
                key={route.key || index}
              />
            );
          }
        }
        return null;
      }
      return null;
    });

  const theme = React.useMemo(() => {
    const primary = themeColor || DEFAULT_THEME_COLOR;
    return createTheme(baseTheme, {
      palette: {
        primary: {
          main: primary,
          focus: primary,
        },
        background: {
          default: "#ffffff",
          paper: "#ffffff",
        },
        gradients: {
          ...baseTheme.palette?.gradients,
          primary: {
            main: primary,
            state: primary,
          },
        },
      },
      components: {
        MuiTableCell: {
          styleOverrides: {
            head: {
              backgroundColor: "transparent",
              background: "none",
            },
          },
        },
        MUIDataTableHeadCell: {
          styleOverrides: {
            root: {
              backgroundColor: "transparent",
              background: "none",
            },
            fixedHeader: {
              backgroundColor: "transparent",
              background: "none",
            },
          },
        },
      },
    });
  }, [themeColor]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <ThemeColorProvider themeColor={themeColor} setThemeColor={setThemeColor}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
      {layout === "dashboard" && (
        <>
          {renderSidenav && (
            <Sidenav
              color="primary"
              brand={brand}
              brandName="Speed Track Solutions"
              routes={privateRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
              logo={logo}
            />
          )}
          <Configurator themeColor={themeColor} setThemeColor={setThemeColor} />
        </>
      )}
      {layout === "vr" && <Configurator themeColor={themeColor} setThemeColor={setThemeColor} />}
      <Routes>
        {renderRoutes(privateRoutes)}
        {user ? (
          <Route
            path="*"
            element={
              <Navigate
                to={user.roleId === 5 ? "/customer/tickets-list" : "/dashboard"}
              />
            }
          />
        ) : (
          <Route path="*" element={<Navigate to="/home" />} />
        )}
        <Route path="/" element={<Navigate to="/home" />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<PasswordReset />} />
        <Route path="/employee/sign-in" element={<AdminSignIn />} />
        <Route path="/home" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-conditions" element={<TermsConditions />} />
        {normalizedPagesData.map((data) => {
          return (
            <Route
              key={data.page_route}
              path={`/${data?.page_route}`}
              element={<ReUsePage pageData={data} />}
            />
          );
        })}
        <Route
          path="/unauthorized"
          element={<Navigate to="/employee/sign-in" />}
        />
        <Route path="*" element={<NotFound user={user} />} />
      </Routes>
      </ThemeProvider>
    </ThemeColorProvider>
  );
}
 